import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { Appliance } from '../../../../api/api-appliance';
import { Circuit } from '../../../../api/api-device';
import { RootState } from '../../../../app/store';

export const NEW_APPLIANCE: Appliance = {
  appliance_id: 0,
  appliance_name: 'New Appliance',
  appliance_type: '',
  control_device_id: null,
  circuits: [],
};

export type ApplianceDisplayConfig = Omit<Appliance, 'circuits'> & {
  circuits: Circuit[];
};

export type CircuitConfigState = {
  selectedMeterId: number | null;
  allCircuits: Circuit[];
  allAppliances: Appliance[];
};

export const initialState: CircuitConfigState = {
  selectedMeterId: null,
  allCircuits: [],
  allAppliances: [],
};

export const circuitApplianceConfigSlice = createSlice({
  name: 'circuitApplianceConfig',
  initialState,
  reducers: {
    setSelectedMeterId: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        selectedMeterId: action.payload,
      };
    },
    setAllCircuits: (state, action: PayloadAction<Circuit[]>) => {
      // We want to update the appliances with their respective circuits
      // This should be the source of truth for updating circuits in appliances
      const newAllAppliances = state.allAppliances.map((appliance) => {
        return {
          ...appliance,
          circuits: action.payload
            .filter((circuit) => circuit.appliance_id === appliance.appliance_id)
            .map((c) => c.id),
        };
      });
      // Update the state with the new circuits and appliances
      return { ...state, allCircuits: action.payload, allAppliances: newAllAppliances };
    },
    setAllAppliances: (state, action: PayloadAction<Appliance[]>) => {
      return { ...state, allAppliances: action.payload };
    },
    addAppliance: (
      state,
      action: PayloadAction<{
        name: string;
        type: string;
      }>
    ) => {
      // create appliance id in negative to avoid conflict with existing appliance ids
      // we can't use null because we need to identify the appliance in the UI
      const totalNegativeApplianceIds = state.allAppliances.filter(
        (applianceDisplayConfig) => applianceDisplayConfig.appliance_id < 0
      ).length;
      const newApplianceId = -1 * (totalNegativeApplianceIds + 1);
      return {
        ...state,
        allAppliances: [
          {
            ...NEW_APPLIANCE,
            appliance_id: newApplianceId,
            appliance_name: action.payload.name,
            appliance_type: action.payload.type,
          },
          ...state.allAppliances,
        ],
      };
    },
    removeAppliance: (state, action: PayloadAction<number>) => {
      // Remove the appliance and set circuits of the removed appliance to null
      return {
        ...state,
        allAppliances: state.allAppliances.filter((appliance) => appliance.appliance_id !== action.payload),
        allCircuits: state.allCircuits.map((circuit) =>
          circuit.appliance_id === action.payload ? { ...circuit, appliance_id: null } : circuit
        ),
      };
    },
    updateAppliance: (
      state,
      action: PayloadAction<{
        applianceId: number;
        name?: string;
        type?: string;
        circuits?: number[];
        controlDeviceId?: number | null;
      }>
    ) => {
      return {
        ...state,
        allAppliances: state.allAppliances.map((appliance) =>
          appliance.appliance_id === action.payload.applianceId
            ? {
                ...appliance,
                appliance_name: action.payload.name ?? appliance.appliance_name,
                appliance_type: action.payload.type ?? appliance.appliance_type,
                circuits: action.payload.circuits ?? appliance.circuits,
                control_device_id:
                  action.payload.controlDeviceId === undefined
                    ? appliance.control_device_id
                    : action.payload.controlDeviceId,
              }
            : appliance
        ),
      };
    },
  },
});

export const { setSelectedMeterId, setAllCircuits, setAllAppliances, addAppliance, removeAppliance, updateAppliance } =
  circuitApplianceConfigSlice.actions;

export const selectSelectedMeterId = (state: RootState) => state.circuitApplianceConfig.selectedMeterId;
export const selectAllCircuits = (state: RootState) => state.circuitApplianceConfig.allCircuits;
export const selectAllAppliances = (state: RootState) => state.circuitApplianceConfig.allAppliances;

export const { reducer: circuitApplianceConfigReducer } = circuitApplianceConfigSlice;
